import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Save,
  Notifications,
  Email,
  Sms,
  DesktopWindows,
  Google,
  LinkedIn,
  Storage,
  Security,
  Person,
  Language,
  Link as LinkIcon,
  Check,
  Close,
} from '@mui/icons-material';
import { baseClient } from '../api/baseClient';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Inline type definitions (avoiding module import issues)
interface NotificationSettings {
  email_notifications: boolean;
  push_notifications: boolean;
  sms_notifications: boolean;
  deal_updates: boolean;
  task_reminders: boolean;
  weekly_reports: boolean;
  mention_alerts: boolean;
  new_leads: boolean;
  marketing_updates: boolean;
  system_alerts: boolean;
}

interface ThemeSettings {
  compact_mode: boolean;
  sidebar_collapsed: boolean;
  dark_mode: boolean;
  font_size: 'small' | 'medium' | 'large';
  color_scheme: 'default' | 'blue' | 'green' | 'purple';
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Profile state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    display_name: '',
    job_title: '',
    department: '',
    phone: '',
    bio: '',
    location: '',
    timezone: 'UTC',
    language_preference: 'en',
  });

  // Password state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    push_notifications: true,
    sms_notifications: false,
    deal_updates: true,
    task_reminders: true,
    weekly_reports: true,
    mention_alerts: true,
    new_leads: true,
    marketing_updates: false,
    system_alerts: true,
  });

  // Integration state
  const [integrations, setIntegrations] = useState({
    google_workspace: false,
    salesforce: false,
    slack: false,
    hubspot: false,
    linkedin: false,
    zapier: false,
  });

  // Theme state
  const [themeSettings, setThemeSettings] = useState({
    compact_mode: false,
    sidebar_collapsed: false,
    dark_mode: false,
    font_size: 'medium' as 'small' | 'medium' | 'large',
    color_scheme: 'default' as 'default' | 'blue' | 'green' | 'purple',
  });

  // Fetch user profile
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['userProfile', 'me'],
    queryFn: async () => {
      try {
        const response = await baseClient.get('/user-profiles/profiles/me');
        return response.data || response;
      } catch (error: any) {
        console.error('Profile fetch error:', error);
        if (error?.response?.status === 404) {
          return {
            first_name: (user as any)?.email?.split('@')[0] || '',
            last_name: '',
            display_name: (user as any)?.email || '',
            email: (user as any)?.email || '',
            job_title: '',
            department: '',
            phone: '',
            bio: '',
            location: '',
            timezone: 'UTC',
            language_preference: 'en',
          };
        }
        throw error;
      }
    },
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (profileData: typeof formData) => {
      try {
        const response = await baseClient.patch('/user-profiles/profiles/me', profileData);
        return response;
      } catch (error: any) {
        console.error('Profile update error:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      alert('Profile updated successfully!');
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (passwordData: typeof passwordForm) => {
      const response = await baseClient.post('/user-profiles/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      return response;
    },
    onSuccess: () => {
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
      alert('Password changed successfully!');
    },
  });

  // Fetch notification settings
  const { data: notificationData } = useQuery({
    queryKey: ['notificationSettings'],
    queryFn: async () => {
      try {
        const response = await baseClient.get<{ settings: NotificationSettings }>('/notifications/settings');
        return response.settings;
      } catch (error) {
        console.error('Notification settings fetch error:', error);
        return null;
      }
    },
  });

  // Update notification settings when loaded
  React.useEffect(() => {
    if (notificationData) {
      setNotificationSettings(prev => ({
        ...prev,
        ...notificationData,
      }));
    }
  }, [notificationData]);

  // Save notification settings mutation
  const saveNotificationsMutation = useMutation({
    mutationFn: async (settings: NotificationSettings) => {
      return baseClient.put('/notifications/settings', settings);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationSettings'] });
      alert('Notification settings saved successfully!');
    },
    onError: (error) => {
      console.error('Failed to save notification settings:', error);
      alert('Failed to save notification settings. Please try again.');
    },
  });

  // Fetch theme/appearance preferences
  const { data: preferencesData } = useQuery({
    queryKey: ['userPreferences'],
    queryFn: async () => {
      try {
        const response = await baseClient.get<{ preferences: { theme: ThemeSettings } }>('/user-profiles/preferences');
        return response.preferences;
      } catch (error) {
        console.error('User preferences fetch error:', error);
        return null;
      }
    },
  });

  // Update theme settings when loaded
  React.useEffect(() => {
    if (preferencesData?.theme) {
      setThemeSettings(prev => ({
        ...prev,
        ...preferencesData.theme,
      }));
    }
  }, [preferencesData]);

  // Save theme settings mutation
  const saveThemeMutation = useMutation({
    mutationFn: async (theme: ThemeSettings) => {
      return baseClient.patch('/user-profiles/preferences/theme', theme);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userPreferences'] });
      alert('Appearance settings saved successfully!');
    },
    onError: (error) => {
      console.error('Failed to save appearance settings:', error);
      alert('Failed to save appearance settings. Please try again.');
    },
  });

  // Update form data when profile loads
  React.useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        display_name: profile.display_name || '',
        job_title: profile.job_title || '',
        department: profile.department || '',
        phone: profile.phone || '',
        bio: profile.bio || '',
        location: profile.location || '',
        timezone: profile.timezone || 'UTC',
        language_preference: profile.language_preference || 'en',
      });
    }
  }, [profile]);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field: keyof typeof passwordForm, value: string) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSaveProfile = () => {
    updateProfileMutation.mutate(formData);
  };

  const handleChangePassword = () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      return;
    }
    changePasswordMutation.mutate(passwordForm);
  };

  const handleSaveNotifications = () => {
    saveNotificationsMutation.mutate(notificationSettings as NotificationSettings);
  };

  const handleSaveTheme = () => {
    saveThemeMutation.mutate(themeSettings as ThemeSettings);
  };

  const handleConnectIntegration = async (integration: string) => {
    // Open OAuth flow in a popup window
    const apiUrl = import.meta.env.VITE_API_URL || '';
    const oauthUrl = `${apiUrl}/integrations/login/${integration}`;
    
    const popup = window.open(
      oauthUrl,
      `${integration}-oauth`,
      'width=600,height=700,left=200,top=100'
    );

    // Listen for popup close and refresh integrations
    if (popup) {
      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          // Refresh integrations status
          queryClient.invalidateQueries({ queryKey: ['integrations'] });
        }
      }, 500);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      <Paper sx={{ mt: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Person />} label="Profile" iconPosition="start" />
          <Tab icon={<Security />} label="Security" iconPosition="start" />
          <Tab icon={<Notifications />} label="Notifications" iconPosition="start" />
          <Tab icon={<LinkIcon />} label="Integrations" iconPosition="start" />
          <Tab icon={<DesktopWindows />} label="Appearance" iconPosition="start" />
        </Tabs>

        {/* Profile Tab */}
        <TabPanel value={tabValue} index={0}>
          {profileLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Personal Information
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="First Name"
                          value={formData.first_name}
                          onChange={(e) => handleInputChange('first_name', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Last Name"
                          value={formData.last_name}
                          onChange={(e) => handleInputChange('last_name', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Display Name"
                          value={formData.display_name}
                          onChange={(e) => handleInputChange('display_name', e.target.value)}
                          helperText="How your name appears to other users"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Job Title"
                          value={formData.job_title}
                          onChange={(e) => handleInputChange('job_title', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Department"
                          value={formData.department}
                          onChange={(e) => handleInputChange('department', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Bio"
                          multiline
                          rows={3}
                          value={formData.bio}
                          onChange={(e) => handleInputChange('bio', e.target.value)}
                          helperText="Tell others about yourself"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Location"
                          value={formData.location}
                          onChange={(e) => handleInputChange('location', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>Timezone</InputLabel>
                          <Select
                            value={formData.timezone}
                            label="Timezone"
                            onChange={(e) => handleInputChange('timezone', e.target.value)}
                          >
                            <MenuItem value="UTC">UTC</MenuItem>
                            <MenuItem value="America/New_York">Eastern Time</MenuItem>
                            <MenuItem value="America/Chicago">Central Time</MenuItem>
                            <MenuItem value="America/Denver">Mountain Time</MenuItem>
                            <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                            <MenuItem value="Europe/London">London</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <CardActions>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleSaveProfile}
                      disabled={updateProfileMutation.isPending}
                    >
                      {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              {updateProfileMutation.isError && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    Failed to update profile. Please try again.
                  </Alert>
                </Grid>
              )}

              {updateProfileMutation.isSuccess && (
                <Grid item xs={12}>
                  <Alert severity="success">
                    Profile updated successfully!
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </TabPanel>

        {/* Security Tab */}
        <TabPanel value={tabValue} index={1}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Change Password
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordForm.current_password}
                    onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={() => togglePasswordVisibility('current')}>
                          {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordForm.new_password}
                    onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={() => togglePasswordVisibility('new')}>
                          {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordForm.confirm_password}
                    onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                    error={
                      passwordForm.confirm_password !== '' &&
                      passwordForm.new_password !== passwordForm.confirm_password
                    }
                    helperText={
                      passwordForm.confirm_password !== '' &&
                      passwordForm.new_password !== passwordForm.confirm_password
                        ? 'Passwords do not match'
                        : ''
                    }
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={() => togglePasswordVisibility('confirm')}>
                          {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                onClick={handleChangePassword}
                disabled={
                  changePasswordMutation.isPending ||
                  !passwordForm.current_password ||
                  !passwordForm.new_password ||
                  passwordForm.new_password !== passwordForm.confirm_password
                }
              >
                {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
              </Button>
            </CardActions>
          </Card>

          {changePasswordMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to change password. Please check your current password.
            </Alert>
          )}

          {changePasswordMutation.isSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Password changed successfully!
            </Alert>
          )}
        </TabPanel>

        {/* Preferences Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Email sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Email Notifications
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Email Notifications" 
                        secondary="Receive updates via email"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notificationSettings.email_notifications}
                          onChange={(e) => setNotificationSettings(prev => ({
                            ...prev,
                            email_notifications: e.target.checked
                          }))}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Weekly Reports" 
                        secondary="Get weekly performance summaries"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notificationSettings.weekly_reports}
                          onChange={(e) => setNotificationSettings(prev => ({
                            ...prev,
                            weekly_reports: e.target.checked
                          }))}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <DesktopWindows sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Push Notifications
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Push Notifications" 
                        secondary="Browser push notifications"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notificationSettings.push_notifications}
                          onChange={(e) => setNotificationSettings(prev => ({
                            ...prev,
                            push_notifications: e.target.checked
                          }))}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="SMS Notifications" 
                        secondary="Receive text messages for urgent alerts"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notificationSettings.sms_notifications}
                          onChange={(e) => setNotificationSettings(prev => ({
                            ...prev,
                            sms_notifications: e.target.checked
                          }))}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Activity Alerts
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={notificationSettings.deal_updates}
                            onChange={(e) => setNotificationSettings(prev => ({
                              ...prev,
                              deal_updates: e.target.checked
                            }))}
                          />
                        }
                        label="Deal Updates"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={notificationSettings.task_reminders}
                            onChange={(e) => setNotificationSettings(prev => ({
                              ...prev,
                              task_reminders: e.target.checked
                            }))}
                          />
                        }
                        label="Task Reminders"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={notificationSettings.mention_alerts}
                            onChange={(e) => setNotificationSettings(prev => ({
                              ...prev,
                              mention_alerts: e.target.checked
                            }))}
                          />
                        }
                        label="Mentions"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={notificationSettings.new_leads}
                            onChange={(e) => setNotificationSettings(prev => ({
                              ...prev,
                              new_leads: e.target.checked
                            }))}
                          />
                        }
                        label="New Leads"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions>
                  <Button 
                    variant="contained" 
                    startIcon={<Save />}
                    onClick={handleSaveNotifications}
                    disabled={saveNotificationsMutation.isPending}
                  >
                    {saveNotificationsMutation.isPending ? 'Saving...' : 'Save Notification Settings'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Integrations Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Connect your CRM with other tools to streamline your workflow.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Google sx={{ fontSize: 40, mr: 2, color: '#4285F4' }} />
                    <Box>
                      <Typography variant="h6">Google Workspace</Typography>
                      <Chip 
                        size="small" 
                        label={integrations.google_workspace ? "Connected" : "Not Connected"}
                        color={integrations.google_workspace ? "success" : "default"}
                        icon={integrations.google_workspace ? <Check /> : <Close />}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Sync contacts, calendar events, and emails with Google Workspace.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    variant={integrations.google_workspace ? "outlined" : "contained"}
                    onClick={() => integrations.google_workspace 
                      ? setIntegrations(prev => ({ ...prev, google_workspace: false }))
                      : handleConnectIntegration('google')
                    }
                  >
                    {integrations.google_workspace ? "Disconnect" : "Connect"}
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Storage sx={{ fontSize: 40, mr: 2, color: '#00A1E0' }} />
                    <Box>
                      <Typography variant="h6">Salesforce</Typography>
                      <Chip 
                        size="small" 
                        label={integrations.salesforce ? "Connected" : "Not Connected"}
                        color={integrations.salesforce ? "success" : "default"}
                        icon={integrations.salesforce ? <Check /> : <Close />}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Import and sync data with Salesforce CRM.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    variant={integrations.salesforce ? "outlined" : "contained"}
                    onClick={() => setIntegrations(prev => ({
                      ...prev,
                      salesforce: !prev.salesforce
                    }))}
                  >
                    {integrations.salesforce ? "Disconnect" : "Connect"}
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <LinkedIn sx={{ fontSize: 40, mr: 2, color: '#0A66C2' }} />
                    <Box>
                      <Typography variant="h6">LinkedIn</Typography>
                      <Chip 
                        size="small" 
                        label={integrations.linkedin ? "Connected" : "Not Connected"}
                        color={integrations.linkedin ? "success" : "default"}
                        icon={integrations.linkedin ? <Check /> : <Close />}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Enrich contact data and track LinkedIn activities.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    variant={integrations.linkedin ? "outlined" : "contained"}
                    onClick={() => setIntegrations(prev => ({
                      ...prev,
                      linkedin: !prev.linkedin
                    }))}
                  >
                    {integrations.linkedin ? "Disconnect" : "Connect"}
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box 
                      component="span" 
                      sx={{ 
                        width: 40, 
                        height: 40, 
                        mr: 2, 
                        bgcolor: '#4A154B', 
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: 20
                      }}
                    >
                      S
                    </Box>
                    <Box>
                      <Typography variant="h6">Slack</Typography>
                      <Chip 
                        size="small" 
                        label={integrations.slack ? "Connected" : "Not Connected"}
                        color={integrations.slack ? "success" : "default"}
                        icon={integrations.slack ? <Check /> : <Close />}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Get notifications and updates directly in Slack.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    variant={integrations.slack ? "outlined" : "contained"}
                    onClick={() => setIntegrations(prev => ({
                      ...prev,
                      slack: !prev.slack
                    }))}
                  >
                    {integrations.slack ? "Disconnect" : "Connect"}
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box 
                      component="span" 
                      sx={{ 
                        width: 40, 
                        height: 40, 
                        mr: 2, 
                        bgcolor: '#FF7A59', 
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: 16
                      }}
                    >
                      HS
                    </Box>
                    <Box>
                      <Typography variant="h6">HubSpot</Typography>
                      <Chip 
                        size="small" 
                        label={integrations.hubspot ? "Connected" : "Not Connected"}
                        color={integrations.hubspot ? "success" : "default"}
                        icon={integrations.hubspot ? <Check /> : <Close />}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Sync marketing data and leads from HubSpot.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    variant={integrations.hubspot ? "outlined" : "contained"}
                    onClick={() => setIntegrations(prev => ({
                      ...prev,
                      hubspot: !prev.hubspot
                    }))}
                  >
                    {integrations.hubspot ? "Disconnect" : "Connect"}
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box 
                      component="span" 
                      sx={{ 
                        width: 40, 
                        height: 40, 
                        mr: 2, 
                        bgcolor: '#FF4F00', 
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: 14
                      }}
                    >
                      ⚡
                    </Box>
                    <Box>
                      <Typography variant="h6">Zapier</Typography>
                      <Chip 
                        size="small" 
                        label={integrations.zapier ? "Connected" : "Not Connected"}
                        color={integrations.zapier ? "success" : "default"}
                        icon={integrations.zapier ? <Check /> : <Close />}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Automate workflows with 5000+ apps via Zapier.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    variant={integrations.zapier ? "outlined" : "contained"}
                    onClick={() => setIntegrations(prev => ({
                      ...prev,
                      zapier: !prev.zapier
                    }))}
                  >
                    {integrations.zapier ? "Disconnect" : "Connect"}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Appearance Tab */}
        <TabPanel value={tabValue} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Display Settings
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <DesktopWindows />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Compact Mode" 
                        secondary="Reduce spacing for more content"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={themeSettings.compact_mode}
                          onChange={(e) => setThemeSettings(prev => ({
                            ...prev,
                            compact_mode: e.target.checked
                          }))}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Storage />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Collapsed Sidebar" 
                        secondary="Start with sidebar collapsed"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={themeSettings.sidebar_collapsed}
                          onChange={(e) => setThemeSettings(prev => ({
                            ...prev,
                            sidebar_collapsed: e.target.checked
                          }))}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Language sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Language & Region
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Language</InputLabel>
                        <Select
                          value={formData.language_preference}
                          label="Language"
                          onChange={(e) => handleInputChange('language_preference', e.target.value)}
                        >
                          <MenuItem value="en">English</MenuItem>
                          <MenuItem value="es">Spanish</MenuItem>
                          <MenuItem value="fr">French</MenuItem>
                          <MenuItem value="de">German</MenuItem>
                          <MenuItem value="pt">Portuguese</MenuItem>
                          <MenuItem value="ja">Japanese</MenuItem>
                          <MenuItem value="zh">Chinese</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Date Format</InputLabel>
                        <Select
                          value="MM/DD/YYYY"
                          label="Date Format"
                        >
                          <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                          <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                          <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Time Format</InputLabel>
                        <Select
                          value="12h"
                          label="Time Format"
                        >
                          <MenuItem value="12h">12 Hour (AM/PM)</MenuItem>
                          <MenuItem value="24h">24 Hour</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions>
                  <Button 
                    variant="contained" 
                    startIcon={<Save />}
                    onClick={handleSaveTheme}
                    disabled={saveThemeMutation.isPending}
                  >
                    {saveThemeMutation.isPending ? 'Saving...' : 'Save Appearance Settings'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default SettingsPage;
